import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import InventoryInvestmentPanel from './components/InventoryInvestmentPanel';
import MarketingBurnRatePanel from './components/MarketingBurnRatePanel';
import CashDurationCalculator from './components/CashDurationCalculator';
import ReorderPointAnalysis from './components/ReorderPointAnalysis';
import MetricsDashboard from './components/MetricsDashboard';
import ScenarioModeling from './components/ScenarioModeling';
import ExportReports from './components/ExportReports';
import { TrendingDown, Calendar, Package, DollarSign, Activity, BarChart3, Settings, HelpCircle, X, ShoppingCart, Save, CheckCircle } from 'lucide-react';
import { cashFlowService } from '../../services/cashFlowService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency as formatCurrencyUtil, getCurrencySymbol } from '../../utils/currencyFormatter';

const CashFlowForecastCalculator = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showHelp, setShowHelp] = useState(false);
  const timelineChartRef = useRef(null);
  
  const [cashFlowData, setCashFlowData] = useState({
    currentCash: '',
    monthlyRevenue: '',
    revenueGrowthRate: 5,
    
    // Inventory Investment
    productCost: '',
    quantityNeeded: '',
    supplierPaymentTerms: 30,
    leadTimeDays: 14,
    safetyStockPercent: 20,
    
    // Marketing Burn Rate
    campaignBudgets: {
      social: '',
      search: '',
      display: '',
      email: ''
    },
    monthlyMarketingSpend: '',
    customerAcquisitionCost: '',
    conversionRate: 2.5,
    
    // Operating Costs
    monthlyOperatingCosts: '',
    staffCosts: '',
    overheadCosts: ''
  });

  const [calculations, setCalculations] = useState({
    totalInventoryInvestment: 0,
    totalMonthlyBurn: 0,
    cashRunwayMonths: 0,
    reorderPoint: 0,
    safetyStockUnits: 0,
    monthlyInventoryTurnover: 0,
    breakEvenPoint: 0,
    criticalCashLevel: 0
  });

  const [scenarioResults, setScenarioResults] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);

  // New states for save functionality
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const saveTimeoutRef = useRef(null);

  // Load saved data on component mount
  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    try {
      setIsLoading(true);
      const savedData = await cashFlowService?.loadForecast();
      
      if (savedData) {
        // Restore all saved data
        setCashFlowData({
          currentCash: savedData?.currentCash,
          monthlyRevenue: savedData?.monthlyRevenue,
          revenueGrowthRate: savedData?.revenueGrowthRate,
          productCost: savedData?.productCost,
          quantityNeeded: savedData?.quantityNeeded,
          supplierPaymentTerms: savedData?.supplierPaymentTerms,
          leadTimeDays: savedData?.leadTimeDays,
          safetyStockPercent: savedData?.safetyStockPercent,
          campaignBudgets: savedData?.campaignBudgets,
          monthlyMarketingSpend: savedData?.monthlyMarketingSpend,
          customerAcquisitionCost: savedData?.customerAcquisitionCost,
          conversionRate: savedData?.conversionRate,
          monthlyOperatingCosts: savedData?.monthlyOperatingCosts,
          staffCosts: savedData?.staffCosts,
          overheadCosts: savedData?.overheadCosts
        });
        
        // Remove currency from saved data - now comes from profile
        setScenarioResults(savedData?.scenarioResults || []);
        setLastSaved(new Date(savedData.updatedAt));
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
      setSaveError('Failed to load saved data');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-save functionality with debounce
  useEffect(() => {
    // Clear existing timeout
    if (saveTimeoutRef?.current) {
      clearTimeout(saveTimeoutRef?.current);
    }

    // Don't save while loading initial data
    if (isLoading) return;

    // Set new timeout for auto-save (2 seconds after last change)
    saveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef?.current) {
        clearTimeout(saveTimeoutRef?.current);
      }
    };
  }, [cashFlowData, scenarioResults]);

  const handleAutoSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      
      // Don't save currency - it comes from profile
      const dataToSave = {
        ...cashFlowData,
        scenarioResults
      };
      
      await cashFlowService?.saveForecast(dataToSave);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error auto-saving:', error);
      setSaveError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      
      // Don't save currency - it comes from profile
      const dataToSave = {
        ...cashFlowData,
        scenarioResults
      };
      
      await cashFlowService?.saveForecast(dataToSave);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving:', error);
      setSaveError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  // Update formatCurrency to use profile currency
  const formatCurrency = (amount) => {
    const currencyCode = profile?.currency || 'ZAR';
    return formatCurrencyUtil(parseNum(amount), currencyCode);
  };

  const calculateCashFlow = () => {
    const currentCash = parseNum(cashFlowData?.currentCash);
    const monthlyRevenue = parseNum(cashFlowData?.monthlyRevenue);
    const productCost = parseNum(cashFlowData?.productCost);
    const quantityNeeded = parseNum(cashFlowData?.quantityNeeded);
    const safetyStockPercent = parseNum(cashFlowData?.safetyStockPercent);
    
    const socialBudget = parseNum(cashFlowData?.campaignBudgets?.social);
    const searchBudget = parseNum(cashFlowData?.campaignBudgets?.search);
    const displayBudget = parseNum(cashFlowData?.campaignBudgets?.display);
    const emailBudget = parseNum(cashFlowData?.campaignBudgets?.email);
    
    const monthlyMarketingSpend = socialBudget + searchBudget + displayBudget + emailBudget;
    const monthlyOperatingCosts = parseNum(cashFlowData?.monthlyOperatingCosts);
    const staffCosts = parseNum(cashFlowData?.staffCosts);
    const overheadCosts = parseNum(cashFlowData?.overheadCosts);

    // Inventory Investment Calculations
    const totalInventoryInvestment = productCost * quantityNeeded;
    const safetyStockUnits = Math.ceil(quantityNeeded * (safetyStockPercent / 100));
    const reorderPoint = Math.ceil(quantityNeeded * 0.3) + safetyStockUnits;
    
    // Cash Burn Calculations
    const totalMonthlyBurn = monthlyMarketingSpend + monthlyOperatingCosts + staffCosts + overheadCosts;
    const netMonthlyCashFlow = monthlyRevenue - totalMonthlyBurn;
    const cashRunwayMonths = totalMonthlyBurn > 0 ? currentCash / totalMonthlyBurn : 0;
    
    // Critical Levels
    const criticalCashLevel = totalMonthlyBurn * 3; // 3 months of operating expenses
    const breakEvenPoint = totalMonthlyBurn;
    const monthlyInventoryTurnover = monthlyRevenue > 0 && productCost > 0 
      ? monthlyRevenue / (productCost * quantityNeeded) 
      : 0;

    return {
      totalInventoryInvestment,
      totalMonthlyBurn,
      cashRunwayMonths,
      reorderPoint,
      safetyStockUnits,
      monthlyInventoryTurnover,
      breakEvenPoint,
      criticalCashLevel,
      netMonthlyCashFlow
    };
  };

  const generateCashFlowProjections = () => {
    const currentCash = parseNum(cashFlowData?.currentCash);
    const monthlyRevenue = parseNum(cashFlowData?.monthlyRevenue);
    const growthRate = parseNum(cashFlowData?.revenueGrowthRate) / 100;
    const totalMonthlyBurn = calculations?.totalMonthlyBurn || 0;

    // Return empty array if no data
    if (!currentCash && !monthlyRevenue) {
      return [];
    }

    const projections = [];
    let remainingCash = currentCash;

    for (let month = 0; month < 12; month++) {
      const projectedRevenue = monthlyRevenue * Math.pow(1 + growthRate, month);
      const cashInflow = projectedRevenue;
      const cashOutflow = totalMonthlyBurn;
      const netCashFlow = cashInflow - cashOutflow;
      
      remainingCash += netCashFlow;
      
      projections?.push({
        month: month + 1,
        revenue: projectedRevenue,
        burn: totalMonthlyBurn,
        netFlow: netCashFlow,
        remainingCash: Math.max(0, remainingCash),
        isCritical: remainingCash < calculations?.criticalCashLevel
      });

      if (remainingCash <= 0) {
        // Fill remaining months with zero cash
        for (let i = month + 1; i < 12; i++) {
          projections?.push({
            month: i + 1,
            revenue: 0,
            burn: totalMonthlyBurn,
            netFlow: -totalMonthlyBurn,
            remainingCash: 0,
            isCritical: true
          });
        }
        break;
      }
    }

    return projections;
  };

  useEffect(() => {
    const calc = calculateCashFlow();
    setCalculations(calc);
  }, [cashFlowData, profile?.currency]);

  useEffect(() => {
    if (calculations?.totalMonthlyBurn !== undefined) {
      const projections = generateCashFlowProjections();
      if (projections?.length > 0) {
        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
          drawCashFlowTimeline(projections);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [calculations, cashFlowData?.currentCash, cashFlowData?.monthlyRevenue, cashFlowData?.revenueGrowthRate, profile?.currency]);

  const drawCashFlowTimeline = (projections) => {
    const canvas = timelineChartRef?.current;
    if (!canvas || !projections?.length) return;

    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    
    const W = canvas.width = canvas?.clientWidth * 2;
    const H = canvas.height = canvas?.clientHeight * 2;

    ctx?.clearRect(0, 0, W, H);

    const pad = 120;
    const maxCash = Math.max(...projections?.map(p => p?.remainingCash), 1);
    const unitX = (W - pad * 2) / 11;
    const unitY = (H - pad * 2) / maxCash;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx?.fillRect(0, 0, W, H);

    // Y-axis labels - use profile currency
    ctx.fillStyle = '#64748b';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'right';
    const symbol = getCurrencySymbol(profile?.currency || 'ZAR');
    
    for (let i = 0; i <= 5; i++) {
      const value = (maxCash / 5) * i;
      const y = H - pad - (i / 5) * (H - pad * 2);
      const formattedValue = `${symbol}${value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      ctx?.fillText(formattedValue, pad - 15, y + 6);
    }

    // Grid lines
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = H - pad - (i / 5) * (H - pad * 2);
      ctx?.beginPath();
      ctx?.moveTo(pad, y);
      ctx?.lineTo(W - pad, y);
      ctx?.stroke();
    }

    // Critical cash level line
    const criticalY = H - pad - (calculations?.criticalCashLevel * unitY);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx?.setLineDash([10, 5]);
    ctx?.beginPath();
    ctx?.moveTo(pad, criticalY);
    ctx?.lineTo(W - pad, criticalY);
    ctx?.stroke();
    ctx?.setLineDash([]);

    ctx.fillStyle = '#ef4444';
    ctx.font = 'bold 14px system-ui';
    ctx.textAlign = 'left';
    ctx?.fillText('Critical Level', pad + 10, criticalY - 10);

    // Area gradient
    const gradient = ctx?.createLinearGradient(0, 0, 0, H);
    gradient?.addColorStop(0, 'rgba(34, 197, 94, 0.2)');
    gradient?.addColorStop(0.5, 'rgba(59, 130, 246, 0.1)');
    gradient?.addColorStop(1, 'rgba(239, 68, 68, 0.1)');
    
    ctx.fillStyle = gradient;
    ctx?.beginPath();
    ctx?.moveTo(pad, H - pad);
    
    projections?.forEach((proj, i) => {
      const x = pad + i * unitX;
      const y = H - pad - (proj?.remainingCash * unitY);
      ctx?.lineTo(x, y);
    });
    
    ctx?.lineTo(pad + (projections?.length - 1) * unitX, H - pad);
    ctx?.closePath();
    ctx?.fill();

    // Main cash flow line
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx?.beginPath();
    
    projections?.forEach((proj, i) => {
      const x = pad + i * unitX;
      const y = H - pad - (proj?.remainingCash * unitY);
      
      if (proj?.isCritical) {
        ctx.strokeStyle = '#ef4444';
      }
      
      if (i === 0) ctx?.moveTo(x, y);
      else ctx?.lineTo(x, y);
    });
    ctx?.stroke();

    // Data points
    projections?.forEach((proj, i) => {
      const x = pad + i * unitX;
      const y = H - pad - (proj?.remainingCash * unitY);
      
      ctx.fillStyle = '#ffffff';
      ctx?.beginPath();
      ctx?.arc(x, y, 8, 0, Math.PI * 2);
      ctx?.fill();
      
      ctx.strokeStyle = proj?.isCritical ? '#ef4444' : '#3b82f6';
      ctx.lineWidth = 3;
      ctx?.beginPath();
      ctx?.arc(x, y, 8, 0, Math.PI * 2);
      ctx?.stroke();
    });

    // Month labels
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    ctx.fillStyle = '#64748b';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'center';
    
    months?.forEach((month, i) => {
      const x = pad + i * unitX;
      ctx?.fillText(month, x, H - 30);
    });
  };

  const handleDataChange = (section, field, value) => {
    if (section === 'campaignBudgets') {
      setCashFlowData(prev => ({
        ...prev,
        campaignBudgets: {
          ...prev?.campaignBudgets,
          [field]: value
        }
      }));
    } else {
      setCashFlowData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const runScenarioAnalysis = (scenarioName, modifications) => {
    const modifiedData = { ...cashFlowData, ...modifications };
    const scenarioCalc = calculateCashFlow();
    
    setScenarioResults(prev => [...prev, {
      name: scenarioName,
      data: modifiedData,
      results: scenarioCalc,
      timestamp: new Date()
    }]);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'analysis', label: 'Analysis', icon: BarChart3 },
    { id: 'scenarios', label: 'Scenarios', icon: TrendingDown },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <Header />
        <main className="ml-64 mt-16 p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading your forecast data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Page Header with Save Status */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">Cash Flow Forecast Calculator</h1>
              <p className="text-sm text-slate-600 mt-1">Strategic cash flow planning for e-commerce businesses</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Save Status Indicator */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-xs text-slate-600">Saving...</span>
                  </>
                ) : lastSaved ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-slate-600">
                      Saved {new Date(lastSaved)?.toLocaleTimeString()}
                    </span>
                  </>
                ) : null}
              </div>

              {/* Manual Save Button */}
              <button
                onClick={handleManualSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                Save Now
              </button>

              <button
                onClick={() => setShowHelp(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <HelpCircle className="w-4 h-4" />
                How to Use
              </button>
            </div>
          </div>

          {/* Error Message */}
          {saveError && (
            <div className="mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          )}
        </div>

        {/* Help Modal */}
        {showHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">How to Use Cash Flow Calculator</h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* E-commerce Quick Start */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <ShoppingCart className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-blue-900 mb-2">Perfect for E-commerce Stores</h3>
                      <p className="text-sm text-blue-800">
                        Works with Shopify, WooCommerce, Magento, or any online store. Connect your sales data and get instant cash flow insights.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step by Step Guide */}
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-4">Step-by-Step Guide</h3>
                  
                  <div className="space-y-4">
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">1. Enter Your Cash Position</h4>
                      <p className="text-sm text-slate-600">
                        Start with your current available cash. This is the money you have in your business account right now.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Example: R 50,000 or $ 3,000</p>
                    </div>

                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">2. Set Monthly Revenue</h4>
                      <p className="text-sm text-slate-600">
                        Enter your average monthly sales. For e-commerce, use your last 3 months average from your store dashboard.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Example: R 100,000/month for Shopify store</p>
                    </div>

                    <div className="border-l-4 border-purple-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">3. Add Inventory Costs</h4>
                      <p className="text-sm text-slate-600">
                        Enter your product costs and quantities. For dropshipping, use supplier costs. For warehoused products, include storage.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Example: Product cost R 200, Quantity 100 units</p>
                    </div>

                    <div className="border-l-4 border-orange-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">4. Configure Marketing Spend</h4>
                      <p className="text-sm text-slate-600">
                        Add your monthly ad spend across channels. Include Facebook Ads, Google Ads, influencer marketing, etc.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Example: Social Media R 10,000, Search Ads R 5,000</p>
                    </div>

                    <div className="border-l-4 border-red-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">5. Include Operating Costs</h4>
                      <p className="text-sm text-slate-600">
                        Add staff costs (employees, freelancers), platform fees, hosting, and overhead (rent, utilities).
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Example: Staff R 20,000, Overhead R 5,000</p>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Explained */}
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-4">Understanding Your Results</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <h4 className="font-semibold text-slate-900 text-sm mb-1">Cash Runway</h4>
                      <p className="text-xs text-slate-600">
                        How many months you can operate with current cash before running out. Aim for 6+ months.
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <h4 className="font-semibold text-slate-900 text-sm mb-1">Burn Rate</h4>
                      <p className="text-xs text-slate-600">
                        Total monthly expenses. Lower is better. Track this to identify cost-cutting opportunities.
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <h4 className="font-semibold text-slate-900 text-sm mb-1">Reorder Point</h4>
                      <p className="text-xs text-slate-600">
                        When to restock inventory. Order before hitting this point to avoid stockouts.
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <h4 className="font-semibold text-slate-900 text-sm mb-1">Critical Level</h4>
                      <p className="text-xs text-slate-600">
                        The red line shows 3 months of operating expenses. Staying above this is crucial.
                      </p>
                    </div>
                  </div>
                </div>

                {/* E-commerce Specific Tips */}
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-4">E-commerce Tips</h3>
                  
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-600">
                        <strong>Shopify/WooCommerce:</strong> Export sales data monthly to track revenue trends
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-600">
                        <strong>Inventory:</strong> Include supplier payment terms for accurate cash timing
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-600">
                        <strong>Marketing:</strong> Track ROAS (Return on Ad Spend) to optimize budget allocation
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-600">
                        <strong>Seasonal Planning:</strong> Use scenarios to model Black Friday, holiday sales spikes
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-600">
                        <strong>Growth Planning:</strong> Adjust revenue growth rate based on your scaling plans
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Signals */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-bold text-red-900 mb-2">⚠️ Warning Signals to Watch</h3>
                  <ul className="space-y-1 text-sm text-red-800">
                    <li>• Cash runway below 3 months - immediate action needed</li>
                    <li>• Burn rate exceeding revenue - unsustainable growth</li>
                    <li>• Inventory turnover below 1x - too much stock</li>
                    <li>• Chart showing red zones - cash running out soon</li>
                  </ul>
                </div>

                {/* Quick Actions */}
                <div className="bg-slate-100 rounded-lg p-4">
                  <h3 className="font-bold text-slate-900 mb-3">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <button className="text-left px-3 py-2 bg-white rounded-lg hover:bg-slate-50 transition-colors text-sm">
                      📊 Run a scenario analysis
                    </button>
                    <button className="text-left px-3 py-2 bg-white rounded-lg hover:bg-slate-50 transition-colors text-sm">
                      💰 Check reorder points
                    </button>
                    <button className="text-left px-3 py-2 bg-white rounded-lg hover:bg-slate-50 transition-colors text-sm">
                      📈 Review 12-month forecast
                    </button>
                    <button className="text-left px-3 py-2 bg-white rounded-lg hover:bg-slate-50 transition-colors text-sm">
                      📤 Export detailed report
                    </button>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4">
                <button
                  onClick={() => setShowHelp(false)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Got it, Let's Start!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs?.map((tab) => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-b-2 border-blue-500 text-blue-600' :'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content - 75% */}
          <div className="lg:col-span-3 space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Key Metrics Banner */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Cash Position</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(cashFlowData?.currentCash)}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-orange-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Burn Rate</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculations?.totalMonthlyBurn)}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-purple-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Runway</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{calculations?.cashRunwayMonths?.toFixed(1)} mo</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Inventory Turnover</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{calculations?.monthlyInventoryTurnover?.toFixed(1)}x</p>
                  </div>
                </div>

                {/* Cash Flow Timeline Chart with Empty State */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-bold text-slate-900">12-Month Cash Flow Projection</h3>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                        <span className="text-xs text-slate-600">Healthy</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                        <span className="text-xs text-slate-600">Critical</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Show empty state if no data */}
                  {!cashFlowData?.currentCash && !cashFlowData?.monthlyRevenue ? (
                    <div className="w-full h-[200px] flex items-center justify-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-slate-600">Enter cash position and revenue to see forecast</p>
                        <p className="text-xs text-slate-500 mt-1">Go to Settings tab to get started</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-[200px]">
                      <canvas ref={timelineChartRef} className="w-full h-full"></canvas>
                    </div>
                  )}
                </div>

                <CashDurationCalculator 
                  data={cashFlowData}
                  onChange={handleDataChange}
                  calculations={calculations}
                  formatCurrency={formatCurrency}
                />
              </>
            )}

            {/* Analysis Tab */}
            {activeTab === 'analysis' && (
              <>
                <ReorderPointAnalysis 
                  data={cashFlowData}
                  calculations={calculations}
                  formatCurrency={formatCurrency}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <InventoryInvestmentPanel 
                    data={cashFlowData}
                    onChange={handleDataChange}
                    calculations={calculations}
                    formatCurrency={formatCurrency}
                  />
                  
                  <MarketingBurnRatePanel 
                    data={cashFlowData}
                    onChange={handleDataChange}
                    calculations={calculations}
                    formatCurrency={formatCurrency}
                  />
                </div>
              </>
            )}

            {/* Scenarios Tab */}
            {activeTab === 'scenarios' && (
              <ScenarioModeling 
                data={cashFlowData}
                scenarios={scenarioResults}
                onRunScenario={runScenarioAnalysis}
                formatCurrency={formatCurrency}
              />
            )}

            {/* Settings Tab with E-commerce Hints */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-900">Calculator Settings</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <ShoppingCart className="w-3.5 h-3.5" />
                    <span>E-commerce optimized</span>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Current Cash Position
                    </label>
                    <input
                      type="text"
                      value={cashFlowData?.currentCash}
                      onChange={(e) => handleDataChange('general', 'currentCash', e?.target?.value)}
                      placeholder="e.g., 50000 (your business account balance)"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Check your Shopify/bank account for current available funds</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Monthly Revenue
                    </label>
                    <input
                      type="text"
                      value={cashFlowData?.monthlyRevenue}
                      onChange={(e) => handleDataChange('general', 'monthlyRevenue', e?.target?.value)}
                      placeholder="e.g., 100000 (average from last 3 months)"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Use average from your store's sales reports</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Revenue Growth Rate (%)
                    </label>
                    <input
                      type="number"
                      value={cashFlowData?.revenueGrowthRate}
                      onChange={(e) => handleDataChange('general', 'revenueGrowthRate', e?.target?.value)}
                      placeholder="5"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Expected monthly growth (5% = steady growth, 10% = aggressive)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Monthly Operating Costs
                    </label>
                    <input
                      type="text"
                      value={cashFlowData?.monthlyOperatingCosts}
                      onChange={(e) => handleDataChange('general', 'monthlyOperatingCosts', e?.target?.value)}
                      placeholder="e.g., 15000 (platform fees, shipping, tools)"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Include Shopify fees, payment processing, shipping fulfillment</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Staff Costs
                    </label>
                    <input
                      type="text"
                      value={cashFlowData?.staffCosts}
                      onChange={(e) => handleDataChange('general', 'staffCosts', e?.target?.value)}
                      placeholder="e.g., 20000 (employees, freelancers, VAs)"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Full-time staff, customer service, virtual assistants</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Overhead Costs
                    </label>
                    <input
                      type="text"
                      value={cashFlowData?.overheadCosts}
                      onChange={(e) => handleDataChange('general', 'overheadCosts', e?.target?.value)}
                      placeholder="e.g., 5000 (rent, utilities, software)"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Office space, software subscriptions, insurance</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - 25% with Sticky Positioning */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-6">
              <MetricsDashboard 
                calculations={calculations}
                data={cashFlowData}
                formatCurrency={formatCurrency}
              />
              
              <ExportReports 
                data={cashFlowData}
                calculations={calculations}
                scenarios={scenarioResults}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CashFlowForecastCalculator;